import React, { useState, useEffect } from 'react';
import type { RotationConfig, RotationExecutionMode } from '../../../types/biweeklyRotation';
import { getExecutionModeStyle } from '../../../types/biweeklyRotation';

interface SettingsTabProps {
  settings: RotationConfig | null;
  loading: boolean;
  saving: boolean;
  onSave: (updates: Partial<RotationConfig>) => Promise<void>;
  onRefresh: () => void;
}

const SettingsTab: React.FC<SettingsTabProps> = ({
  settings,
  loading,
  saving,
  onSave,
  onRefresh,
}) => {
  const [localSettings, setLocalSettings] = useState<Partial<RotationConfig>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings) {
      setLocalSettings({
        executionMode: settings.executionMode,
        isActive: settings.isActive,
        schedule: { ...settings.schedule },
        limits: { ...settings.limits },
      });
      setHasChanges(false);
    }
  }, [settings]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-neutral-400">Loading settings...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 bg-neutral-800/40 py-12 text-center">
        <p className="text-neutral-400">Failed to load settings</p>
        <button onClick={onRefresh} className="mt-4 btn btn-outline px-4 py-2" type="button">
          Retry
        </button>
      </div>
    );
  }

  const updateSetting = <K extends keyof RotationConfig>(
    key: K,
    value: RotationConfig[K]
  ) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const updateSchedule = (key: string, value: string | number) => {
    setLocalSettings((prev) => ({
      ...prev,
      schedule: { ...settings.schedule, ...prev.schedule, [key]: value },
    }));
    setHasChanges(true);
  };

  const updateLimits = (key: string, value: number) => {
    setLocalSettings((prev) => ({
      ...prev,
      limits: { ...settings.limits, ...prev.limits, [key]: value },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onSave(localSettings);
    setHasChanges(false);
  };

  const handleCancel = () => {
    setLocalSettings({
      executionMode: settings.executionMode,
      isActive: settings.isActive,
      schedule: { ...settings.schedule },
      limits: { ...settings.limits },
    });
    setHasChanges(false);
  };

  const executionModes: { mode: RotationExecutionMode; label: string; description: string }[] = [
    {
      mode: 'AUTOMATIC',
      label: 'Automatic',
      description: 'Rotations execute automatically without approval',
    },
    {
      mode: 'MANUAL',
      label: 'Manual Review',
      description: 'Rotations require admin approval before execution',
    },
    {
      mode: 'PREVIEW',
      label: 'Preview Only',
      description: 'Plans are generated but not executed (view-only mode)',
    },
  ];

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
  ];

  return (
    <div className="space-y-6">
      {/* Save Bar (shown when changes exist) */}
      {hasChanges && (
        <div className="sticky top-0 z-10 rounded-2xl border border-cyan-500/30 bg-cyan-900/30 p-4 flex items-center justify-between">
          <span className="text-cyan-100">You have unsaved changes</span>
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              disabled={saving}
              className="btn btn-outline px-4 py-2"
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn px-4 py-2 bg-cyan-500 text-black hover:brightness-110 disabled:opacity-50"
              type="button"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Execution Mode */}
      <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
        <h3 className="text-lg font-semibold text-white mb-2">Execution Mode</h3>
        <p className="text-sm text-neutral-400 mb-4">
          Choose how rotation plans are handled when scheduled
        </p>

        <div className="space-y-3">
          {executionModes.map(({ mode, label, description }) => {
            const style = getExecutionModeStyle(mode);
            const isSelected = localSettings.executionMode === mode;

            return (
              <label
                key={mode}
                className={`flex items-start gap-4 rounded-xl p-4 cursor-pointer transition-all border ${
                  isSelected
                    ? `${style.bgColor} ${style.borderColor}`
                    : 'bg-neutral-900/50 border-white/10 hover:bg-neutral-800/50'
                }`}
              >
                <input
                  type="radio"
                  name="executionMode"
                  value={mode}
                  checked={isSelected}
                  onChange={() => updateSetting('executionMode', mode)}
                  className="mt-1"
                />
                <div>
                  <p className={`font-medium ${isSelected ? style.textColor : 'text-white'}`}>
                    {label}
                  </p>
                  <p className="text-sm text-neutral-400">{description}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Active Toggle */}
      <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Rotation Status</h3>
            <p className="text-sm text-neutral-400 mt-1">
              Enable or disable the biweekly rotation system globally
            </p>
          </div>
          <button
            onClick={() => updateSetting('isActive', !localSettings.isActive)}
            className={`relative w-14 h-8 rounded-full transition-colors ${
              localSettings.isActive ? 'bg-emerald-500' : 'bg-neutral-600'
            }`}
            type="button"
          >
            <span
              className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${
                localSettings.isActive ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Schedule */}
      <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
        <h3 className="text-lg font-semibold text-white mb-2">Schedule</h3>
        <p className="text-sm text-neutral-400 mb-4">
          Configure when rotations occur
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Cadence (days)</label>
            <input
              type="number"
              min="7"
              max="30"
              value={localSettings.schedule?.cadenceDays || settings.schedule.cadenceDays}
              onChange={(e) => updateSchedule('cadenceDays', parseInt(e.target.value) || 14)}
              className="w-full rounded-lg bg-neutral-900 border border-white/10 px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
            />
            <p className="text-xs text-neutral-500 mt-1">How often rotations occur</p>
          </div>

          <div>
            <label className="block text-sm text-neutral-400 mb-1">Day of Week</label>
            <select
              value={localSettings.schedule?.dayOfWeek || settings.schedule.dayOfWeek}
              onChange={(e) => updateSchedule('dayOfWeek', e.target.value)}
              className="w-full rounded-lg bg-neutral-900 border border-white/10 px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
            >
              {daysOfWeek.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-neutral-400 mb-1">Execution Time (ET)</label>
            <input
              type="time"
              value={localSettings.schedule?.executionTime || settings.schedule.executionTime}
              onChange={(e) => updateSchedule('executionTime', e.target.value)}
              className="w-full rounded-lg bg-neutral-900 border border-white/10 px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
            />
            <p className="text-xs text-neutral-500 mt-1">Eastern Time</p>
          </div>
        </div>
      </div>

      {/* Risk Limits */}
      <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
        <h3 className="text-lg font-semibold text-white mb-2">Risk Limits</h3>
        <p className="text-sm text-neutral-400 mb-4">
          Configure guardrails that rotation plans must respect
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-neutral-400 mb-1">Max Turnover (%)</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="10"
                max="50"
                value={(localSettings.limits?.maxTurnoverPct || settings.limits.maxTurnoverPct) * 100}
                onChange={(e) => updateLimits('maxTurnoverPct', parseInt(e.target.value) / 100)}
                className="flex-1"
              />
              <span className="w-12 text-right text-white font-mono">
                {((localSettings.limits?.maxTurnoverPct || settings.limits.maxTurnoverPct) * 100).toFixed(0)}%
              </span>
            </div>
            <p className="text-xs text-neutral-500 mt-1">Maximum portfolio turnover per rotation</p>
          </div>

          <div>
            <label className="block text-sm text-neutral-400 mb-1">Max Cost (bps)</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="10"
                max="100"
                value={localSettings.limits?.maxCostBps || settings.limits.maxCostBps}
                onChange={(e) => updateLimits('maxCostBps', parseInt(e.target.value))}
                className="flex-1"
              />
              <span className="w-12 text-right text-white font-mono">
                {localSettings.limits?.maxCostBps || settings.limits.maxCostBps}
              </span>
            </div>
            <p className="text-xs text-neutral-500 mt-1">Maximum estimated trading cost</p>
          </div>

          <div>
            <label className="block text-sm text-neutral-400 mb-1">Min Cash Buffer (%)</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="5"
                max="20"
                value={(localSettings.limits?.minCashBufferPct || settings.limits.minCashBufferPct) * 100}
                onChange={(e) => updateLimits('minCashBufferPct', parseInt(e.target.value) / 100)}
                className="flex-1"
              />
              <span className="w-12 text-right text-white font-mono">
                {((localSettings.limits?.minCashBufferPct || settings.limits.minCashBufferPct) * 100).toFixed(0)}%
              </span>
            </div>
            <p className="text-xs text-neutral-500 mt-1">Minimum cash to maintain</p>
          </div>

          <div>
            <label className="block text-sm text-neutral-400 mb-1">Max Gross Exposure (%)</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="50"
                max="100"
                value={(localSettings.limits?.maxGrossExposurePct || settings.limits.maxGrossExposurePct) * 100}
                onChange={(e) => updateLimits('maxGrossExposurePct', parseInt(e.target.value) / 100)}
                className="flex-1"
              />
              <span className="w-12 text-right text-white font-mono">
                {((localSettings.limits?.maxGrossExposurePct || settings.limits.maxGrossExposurePct) * 100).toFixed(0)}%
              </span>
            </div>
            <p className="text-xs text-neutral-500 mt-1">Maximum total portfolio exposure</p>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="rounded-2xl border border-white/10 bg-neutral-800/40 p-6">
        <h4 className="text-sm font-semibold text-neutral-300 mb-2">About Biweekly Rotation</h4>
        <div className="text-sm text-neutral-400 space-y-2">
          <p>
            The biweekly rotation system automatically rebalances portfolios by exiting 
            underperforming positions and adding top candidates from the Trading Universe.
          </p>
          <p>
            <strong className="text-neutral-300">Automatic mode</strong> executes without intervention. 
            Use this for hands-off operation once you trust the system.
          </p>
          <p>
            <strong className="text-neutral-300">Manual mode</strong> generates plans that require 
            admin approval. Good for reviewing decisions before execution.
          </p>
          <p>
            <strong className="text-neutral-300">Preview mode</strong> shows what would happen 
            without executing any trades. Useful for testing and monitoring.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
